// src/pages/Jobs/JobCard.tsx
import React, { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardBody, Badge } from 'reactstrap';
import { MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Job } from '../../models/Job.interface';

interface JobCardProps {
  job: Job;
  isHighlighted?: boolean;
  onHover?: (job: Job | null) => void;
  onClick?: (job: Job) => void;
  showDistance?: boolean;
  distance?: number;
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  isHighlighted = false,
  onHover,
  onClick,
  showDistance = false,
  distance
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();
  const params = useParams();

  // Extract image attachments for carousel functionality
  const imageAttachments = useMemo(() => {
    if (!job.jobAttachments) return [];
    return job.jobAttachments.filter(attachment =>
      attachment.url && /\.(jpg|jpeg|png|gif|heic)/i.test(attachment.url)
    );
  }, [job.jobAttachments]);

  // Generate SEO-friendly URL
  const seoUrl = job.jobTitle?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'job';
  const jobDetailPath = `/job/${seoUrl}/${job.id}`;

  // Handle mouse enter/leave for map highlighting
  const handleMouseEnter = () => {
    if (onHover) onHover(job);
  };

  const handleMouseLeave = () => {
    if (onHover) onHover(null);
  };

  // Handle category click
  const handleCategoryClick = (e: React.MouseEvent, category: string) => {
    e.stopPropagation(); // Prevent any parent click handlers
    
    // Get current URL parameters
    const { state, city } = params;
    
    if (state && city) {
      // Convert category to URL-friendly format
      const categorySlug = category.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      // Navigate to filtered page
      const categoryUrl = `/jobs/${state}/${city}/${categorySlug}`;
      navigate(categoryUrl);
    }
  };

  // Image carousel navigation
  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (imageAttachments.length > 0) {
      setCurrentImageIndex(prev => (prev + 1) % imageAttachments.length);
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (imageAttachments.length > 0) {
      setCurrentImageIndex(prev => (prev - 1 + imageAttachments.length) % imageAttachments.length);
    }
  };

  const formatTimeAgo = (date: any) => {
    if (!date) return '';
    
    const now = new Date();
    const jobDate = date.toDate ? date.toDate() : new Date(date);
    const diffInHours = Math.floor((now.getTime() - jobDate.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just posted';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    
    return jobDate.toLocaleDateString();
  };

  // Format job summary to display only 6 lines (approximately 400 characters)
  const formatJobSummary = (summary: string) => {
    if (!summary) return '';
    // Approximate 70 characters per line for 6 lines
    const maxLength = 420;
    if (summary.length <= maxLength) return summary;
    return summary.substring(0, maxLength).trim() + '...';
  };

  // Format the address line (street address)
  const formatAddressLine = (address: any) => {
    if (!address) return 'Address not available';
    
    // Handle both address1 and line1 field names
    if (address.address1) return address.address1;
    if (address.line1) return address.line1;
    
    return 'Address not available';
  };

  // Format the city, state, zip line
  const formatCityStateZip = (address: any) => {
    if (!address) return '';
    
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode) parts.push(address.zipCode);
    
    return parts.join(', ');
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return '#6c757d';
      case 'published':
        return '#0d6efd';
      case 'hiring':
        return '#198754';
      case 'pending inspection':
        return '#ffc107';
      case 'under contract':
        return '#0dcaf0';
      case 'job in progress':
        return '#fd7e14';
      case 'complete':
        return '#198754';
      case 'cancelled':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <Card 
      className={`h-100 shadow-sm border-0 job-card ${isHighlighted ? 'border-primary shadow' : ''}`}
      style={{ 
        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHighlighted ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isHighlighted 
          ? '0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)' 
          : '0 4px 12px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Job Title Section - Full width, 2 lines max */}
        <div className="mb-3">
          <h5 
            className="mb-2 fw-bold text-dark"
            style={{ 
              lineHeight: '1.3',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.6em',
              fontSize: '1.1rem'
            }}
          >
            <Link 
              to={jobDetailPath}
              className="text-decoration-none text-dark"
              style={{ cursor: 'pointer' }}
            >
              {job.jobTitle || 'Untitled Job'}
            </Link>
          </h5>
          
          {/* Created Date */}
          <div className="d-flex align-items-center text-muted" style={{ fontSize: '0.875rem' }}>
            <Clock size={14} className="me-1" />
            <span style={{ minHeight: '1em' }}>{formatTimeAgo(job.dtCreated)}</span>
          </div>
        </div>

        {/* Image Carousel Section - Fixed height */}
        <div className="mb-3" style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
          {imageAttachments.length > 0 ? (
            <div className="position-relative h-100">
              <img
                src={imageAttachments[currentImageIndex].url}
                alt={`Job ${currentImageIndex + 1}`}
                className="w-100 h-100"
                style={{ objectFit: 'cover' }}
              />
              
              {/* Carousel Controls - Only show if multiple images */}
              {imageAttachments.length > 1 && (
                <>
                  <button
                    className="btn btn-light btn-sm position-absolute top-50 start-0 translate-middle-y"
                    style={{ 
                      opacity: 0.9, 
                      border: 'none', 
                      zIndex: 2,
                      marginLeft: '8px',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={prevImage}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    className="btn btn-light btn-sm position-absolute top-50 end-0 translate-middle-y"
                    style={{ 
                      opacity: 0.9, 
                      border: 'none', 
                      zIndex: 2,
                      marginRight: '8px',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={nextImage}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                  >
                    <ChevronRight size={16} />
                  </button>
                  
                  {/* Image counter */}
                  <div 
                    className="position-absolute bottom-0 end-0 bg-dark text-white px-2 py-1"
                    style={{ 
                      fontSize: '0.75rem', 
                      opacity: 0.8,
                      borderTopLeftRadius: '4px'
                    }}
                  >
                    {currentImageIndex + 1} / {imageAttachments.length}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div 
              className="d-flex align-items-center justify-content-center h-100 text-muted"
              style={{ 
                minHeight: '200px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}
            >
              <span style={{ fontSize: '0.875rem' }}>No images available</span>
            </div>
          )}
        </div>

        {/* Address Section - Two lines with improved styling */}
        <div className="mb-3" style={{ minHeight: '3em' }}>
          <div className="d-flex align-items-start">
            <MapPin size={16} className="me-2 mt-1 flex-shrink-0 text-primary" />
            <div style={{ fontSize: '0.875rem' }}>
              <div className="fw-semibold text-dark mb-1" style={{ lineHeight: '1.4' }}>
                {formatAddressLine(job.propertyAddress)}
              </div>
              <div className="text-muted" style={{ lineHeight: '1.4' }}>
                {formatCityStateZip(job.propertyAddress)}
              </div>
            </div>
          </div>
        </div>

        {/* Job Summary Section - Exactly 6 lines, prominent text */}
        <div className="mb-1 flex-grow-1">
          <p 
            className="mb-0 text-dark"
            style={{ 
              lineHeight: '1.5',
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '9em',
              maxHeight: '9em',
              fontSize: '0.9rem'
            }}
          >
            {formatJobSummary(job.jobSummary || 'No description available.')}
          </p>
        </div>

        {/* Job Categories Section */}
        {job.jobCategories && job.jobCategories.length > 0 && (
          <div className="mb-1">
            <div className="d-flex flex-wrap gap-1">
              {job.jobCategories.slice(0, 3).map((category, index) => (
                <Badge
                  key={index}
                  color="light"
                  className="text-primary border border-primary-subtle"
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: '500',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={(e) => handleCategoryClick(e, category)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#6366f1';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#6366f1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.color = '';
                    e.currentTarget.style.borderColor = '';
                  }}
                >
                  {category}
                </Badge>
              ))}
              {job.jobCategories.length > 3 && (
                <span 
                  className="text-muted"
                  style={{ 
                    fontSize: '0.7rem', 
                    alignSelf: 'center',
                    marginLeft: '0.25rem'
                  }}
                >
                  +{job.jobCategories.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Divider Line */}
        <hr style={{ margin: '0.5rem 0', opacity: 0.2, borderColor: '#dee2e6' }} />

        {/* Footer Section - Status and Button */}
        <div className="d-flex justify-content-between align-items-center mt-auto">
          {/* Job Status - Left aligned */}
          <div>
            {job.jobStatus && (
              <span
                style={{
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: getStatusColor(job.jobStatus),
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                {job.jobStatus === 'Job In Progress' ? 'In Progress' : job.jobStatus}
              </span>
            )}
          </div>
          
          {/* View Job Details Button - Right aligned */}
          <Link 
            to={jobDetailPath}
            className="btn btn-primary btn-sm"
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              padding: '0.5rem 0.6rem',
              borderRadius: '6px',
              textDecoration: 'none',
              backgroundColor: '#6366f1',
              borderColor: '#6366f1',
              color: 'white'
            }}
          >
            View Job Details
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default JobCard;